import {
  doc,
  collection,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
  limit,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '@/firebase'
import { RULE_EXPECTATIONS, probe } from '../ruleExpectations.js'

// Live server-side permission checks for the CURRENT signed-in role. Each
// probe attempts a real operation and passes when the server's answer matches
// the rules mirror — whether that answer is "allowed" or "denied". A denial
// where the role should have access fails; access where the role should be
// denied fails LOUDLY (that's a security bug). Anything a probe creates is
// tagged and deleted immediately.
//
// Run this signed in as each test-role account to build per-role coverage.
export default {
  id: 'permissionProbes',
  label: 'Live permission probes (current role)',
  description:
    'Attempts real allowed AND forbidden operations, asserting the server matches the rules for your role.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const tag = { __smokeTest: true, __smokeRunId: ctx.runId }

    await step(`student create (expected: ${fmt(RULE_EXPECTATIONS.studentCreate(role))})`, () => {
      const ref = doc(collection(db, 'students'))
      return probe(
        RULE_EXPECTATIONS.studentCreate(role),
        async () => {
          await setDoc(ref, {
            app: {
              studentData: { firstName: 'SMOKE', lastName: 'Probe', plan: '504' },
              accommodations: { instruction: '', assessment: '' },
              schedule: { classServices: [] },
              staffIds: []
            },
            ...tag,
            createdAt: serverTimestamp(),
            ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
          })
          // Best-effort immediate cleanup (delete may be denied for non-super-admin
          // roles even when create succeeded — the panel's cleanup handles leftovers).
          await deleteDoc(ref).catch(() => {})
        },
        { onUnexpectedSuccess: () => deleteDoc(ref).catch(() => {}) }
      )
    })

    await step(
      `app settings write (expected: ${fmt(RULE_EXPECTATIONS.appSettingsWrite(role))})`,
      () => {
        // Probe against a dedicated doc — NEVER app_settings/global — so a
        // granted write can't disturb real configuration.
        const ref = doc(db, 'app_settings', 'smoke_probe')
        return probe(
          RULE_EXPECTATIONS.appSettingsWrite(role),
          async () => {
            await setDoc(ref, { ...tag, note: 'permission probe — safe to delete' })
            await deleteDoc(ref).catch(() => {})
          },
          { onUnexpectedSuccess: () => deleteDoc(ref).catch(() => {}) }
        )
      }
    )

    const expectAudit = RULE_EXPECTATIONS.auditLogRead(role, {
      schoolId: ctx.schoolId,
      tokenSchoolIds: ctx.tokenSchoolIds
    })
    if (expectAudit === null) {
      step.block(
        'audit log read',
        'Select a school to resolve audit-log scope for this school-tier token'
      )
    } else {
      await step(`audit log read (expected: ${fmt(expectAudit)})`, () => {
        return probe(expectAudit, async () => {
          const q = ctx.schoolId
            ? query(collection(db, 'auditLogs'), where('schoolId', '==', ctx.schoolId), limit(1))
            : query(collection(db, 'auditLogs'), limit(1))
          await getDocs(q)
        })
      })
    }

    await step(
      `feedback template create (expected: ${fmt(RULE_EXPECTATIONS.feedbackTemplateCreate(role))})`,
      () => {
        const ref = doc(collection(db, 'feedbackTemplates'))
        return probe(
          RULE_EXPECTATIONS.feedbackTemplateCreate(role),
          async () => {
            await setDoc(ref, {
              title: 'SMOKE probe',
              active: false,
              shared: false,
              createdBy: ctx.user.uid,
              ...tag,
              createdAt: serverTimestamp(),
              ...(ctx.schoolId ? { schoolId: ctx.schoolId } : {})
            })
            await deleteDoc(ref).catch(() => {})
          },
          { onUnexpectedSuccess: () => deleteDoc(ref).catch(() => {}) }
        )
      }
    )

    // Both eval types: the strict role-type split means expected answers can
    // differ per type (admin_504 → 504 only; sped roles / psych → IEP only).
    for (const evalType of ['IEP', '504']) {
      const expectEval = RULE_EXPECTATIONS.studentEvaluationCreate(role, { evalType })
      const label = `evaluation create ${evalType} (expected: ${fmt(expectEval)})`
      if (!ctx.schoolId) {
        step.block(label, 'Select a school before probing evaluations')
        continue
      }
      await step(label, () => {
        const ref = doc(collection(db, 'studentEvaluations'))
        return probe(
          expectEval,
          async () => {
            await setDoc(ref, {
              studentId: `smoke-eval-student-${ctx.runId.slice(-8)}`,
              schoolId: ctx.schoolId,
              evalType,
              caseManagerId: ctx.user.uid,
              psychId: '',
              isOpen: true,
              dates: {
                referral: '2026-08-01',
                planSent: '',
                consent: '',
                due: '',
                meeting: '',
                closed: ''
              },
              meetingId: '',
              outcome: '',
              changeRequest: null,
              source: 'manual',
              seisStatusSeen: '',
              createdBy: ctx.user.uid,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              ...tag
            })
            await deleteDoc(ref).catch(() => {})
          },
          { onUnexpectedSuccess: () => deleteDoc(ref).catch(() => {}) }
        )
      })
    }

    const expectUserCreate = RULE_EXPECTATIONS.userCreate(role, {
      governance: ctx.userGovernance
    })
    await step(`user create (expected: ${fmt(expectUserCreate)})`, () => {
      // Fake uid prefixed smoke_ — syncUserClaims handles nonexistent Auth
      // users gracefully (logs "not found", no side effects).
      const ref = doc(db, 'users', `smoke_probe_${ctx.runId.slice(-8)}`)
      return probe(
        expectUserCreate,
        async () => {
          await setDoc(ref, {
            name: 'SMOKE Probe User',
            email: `smoke-probe@example.invalid`,
            role: 'teacher',
            status: 'active',
            ...(ctx.schoolId ? { schoolIds: [ctx.schoolId], schoolId: ctx.schoolId } : {}),
            ...tag,
            createdAt: new Date().toISOString()
          })
          await deleteDoc(ref).catch(() => {})
        },
        { onUnexpectedSuccess: () => deleteDoc(ref).catch(() => {}) }
      )
    })
  }
}

function fmt(expected) {
  if (expected === 'depends') return 'governance-dependent'
  return expected ? 'allowed' : 'denied'
}
